// ThreadService.js - Recursively fetch and parse Twitter threads
class ThreadService {
  constructor() {
    this.apiKey = '9f8137c95a0a4753b9b3f39a2f2dfe6c';
    this.baseUrl = '/api/thread-context'; // Use Netlify function proxy
  }

  // Main function to fetch complete thread starting from a tweet ID
  async fetchCompleteThread(tweetId) {
    const allTweets = [];
    const processedIds = new Set();
    const fetchedIds = new Set(); // Track which IDs we've already fetched thread context for
    let apiCallCount = 0;
    const MAX_API_CALLS = 20; // Prevent infinite loops
    let originalAuthorId = null; // Track the original author to filter out other people's tweets
    
    try {
      console.log(`🧵 Starting thread fetch for tweet ID: ${tweetId}`);
      
      // Fetch the initial tweet and its context
      const initialResponse = await this.fetchThreadContext(tweetId);
      apiCallCount++;
      fetchedIds.add(tweetId);
      
      if (!initialResponse.success) {
        throw new Error(initialResponse.error);
      }
      
      // Find the original tweet to get the author ID
      const originalTweet = initialResponse.tweets.find(tweet => tweet.id === tweetId);
      if (originalTweet) {
        originalAuthorId = originalTweet.author.id;
        console.log(`👤 Original author ID: ${originalAuthorId} (@${originalTweet.author.userName})`);
      }
      
      // Process all tweets from the initial response
      const tweetsToProcess = [...initialResponse.tweets];
      
      while (tweetsToProcess.length > 0 && apiCallCount < MAX_API_CALLS) {
        const currentTweet = tweetsToProcess.shift();
        
        // Skip if already processed
        if (processedIds.has(currentTweet.id)) {
          continue;
        }
        
        // Skip if not from the original author (filter out other people's tweets)
        if (originalAuthorId && currentTweet.author.id !== originalAuthorId) {
          console.log(`⏩ Skipping tweet ${currentTweet.id} from @${currentTweet.author.userName} (not original author)`);
          continue;
        }
        
        // Add to processed set
        processedIds.add(currentTweet.id);
        
        // Parse and add tweet to collection
        const parsedTweet = this.parseTweetData(currentTweet);
        allTweets.push(parsedTweet);
        
        console.log(`📝 Processed tweet ${currentTweet.id}: "${parsedTweet.text.slice(0, 50)}..."`);
        
        // Fetch thread context for this tweet to find any replies TO it (if not already fetched)
        if (!fetchedIds.has(currentTweet.id) && apiCallCount < MAX_API_CALLS) {
          console.log(`🔄 Fetching thread context for tweet ${currentTweet.id} to find replies`);
          
          const repliesResponse = await this.fetchThreadContext(currentTweet.id);
          apiCallCount++;
          fetchedIds.add(currentTweet.id);
          
          if (repliesResponse.success) {
            // Add new tweets to processing queue (still add all tweets for processing, filtering happens above)
            repliesResponse.tweets.forEach(tweet => {
              if (!processedIds.has(tweet.id)) {
                tweetsToProcess.push(tweet);
              }
            });
          }
        }
      }
      
      // Sort tweets chronologically
      allTweets.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      
      console.log(`✅ Thread fetch complete! Found ${allTweets.length} tweets (${apiCallCount} API calls made)`);
      
      return {
        success: true,
        tweets: allTweets,
        threadStats: {
          totalTweets: allTweets.length,
          totalLikes: allTweets.reduce((sum, tweet) => sum + tweet.metrics.likeCount, 0),
          totalRetweets: allTweets.reduce((sum, tweet) => sum + tweet.metrics.retweetCount, 0),
          totalReplies: allTweets.reduce((sum, tweet) => sum + tweet.metrics.replyCount, 0)
        }
      };
      
    } catch (error) {
      console.error('❌ Thread fetch failed:', error);
      return {
        success: false,
        error: error.message,
        tweets: []
      };
    }
  }

  // Fetch thread context for a specific tweet ID
  async fetchThreadContext(tweetId) {
    try {
      console.log(`🔍 Fetching thread context for: ${tweetId}`);
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tweetId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      if (!data.tweets || data.tweets.length === 0) {
        throw new Error('No tweets found in thread context');
      }

      return {
        success: true,
        tweets: data.tweets,
        hasNextPage: data.has_next_page,
        nextCursor: data.next_cursor
      };
      
    } catch (error) {
      console.error(`❌ Failed to fetch thread context for ${tweetId}:`, error);
      return {
        success: false,
        error: error.message,
        tweets: []
      };
    }
  }

  // Parse tweet data into a consistent format
  parseTweetData(tweet) {
    // Extract media URLs from the tweet (similar to TwitterImporter logic)
    const mediaUrls = [];
    if (tweet.extendedEntities?.media?.length > 0) {
      mediaUrls.push(...tweet.extendedEntities.media.map(media => media.media_url_https));
    } else if (tweet.entities?.media?.length > 0) {
      mediaUrls.push(...tweet.entities.media.map(media => media.media_url_https));
    }

    // Clean up tweet text by removing t.co URLs if media is present (same logic as TwitterImporter)
    let cleanedText = tweet.text;
    if (tweet.extendedEntities?.media?.length > 0) {
      // Remove all t.co URLs that correspond to media
      tweet.extendedEntities.media.forEach(media => {
        if (media.url) {
          cleanedText = cleanedText.replace(media.url, '').trim();
        }
      });
    } else if (tweet.entities?.media?.length > 0) {
      // Fallback to entities.media if extendedEntities is not available
      tweet.entities.media.forEach(media => {
        if (media.url) {
          cleanedText = cleanedText.replace(media.url, '').trim();
        }
      });
    }

    return {
      id: tweet.id,
      text: cleanedText, // Use cleaned text instead of original
      originalText: tweet.text, // Preserve original text for reference
      author: {
        id: tweet.author.id,
        name: tweet.author.name,
        username: tweet.author.userName,
        profilePicture: tweet.author.profilePicture,
        isVerified: tweet.author.isVerified,
        isBlueVerified: tweet.author.isBlueVerified
      },
      metrics: {
        retweetCount: tweet.retweetCount || 0,
        replyCount: tweet.replyCount || 0,
        likeCount: tweet.likeCount || 0,
        quoteCount: tweet.quoteCount || 0,
        viewCount: tweet.viewCount || 0,
        bookmarkCount: tweet.bookmarkCount || 0
      },
      createdAt: tweet.createdAt,
      isReply: tweet.isReply,
      inReplyToId: tweet.inReplyToId,
      inReplyToUsername: tweet.inReplyToUsername,
      conversationId: tweet.conversationId,
      url: tweet.url,
      twitterUrl: tweet.twitterUrl,
      mediaUrls: mediaUrls, // Add media URLs array
      extendedEntities: tweet.extendedEntities, // Preserve original extended entities
      entities: tweet.entities // Preserve original entities
    };
  }

  // Generate a formatted thread text for copying
  generateThreadText(tweets) {
    if (!tweets || tweets.length === 0) {
      return '';
    }

    // Helper function to format text correctly (t.co URLs already removed in parseTweetData)
    const formatTweetText = (text) => {
      let processedMessage = text;
      
      // Remove any remaining http/https links (as backup)
      processedMessage = processedMessage.replace(/https?:\/\/[^\s]+/g, '');
      // Remove any remaining t.co links (as backup)
      processedMessage = processedMessage.replace(/t\.co\/[^\s]+/g, '');
      // Remove any remaining URLs
      processedMessage = processedMessage.replace(/[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}/g, '');

      // Enhanced preprocessing for Twitter API text formatting (from ThreadGenerator.js's renderMessage)
      processedMessage = processedMessage.replace(/\r\n/g, '\n'); // Windows line breaks
      processedMessage = processedMessage.replace(/\r/g, '\n');   // Mac line breaks
      
      // Handle numbered list patterns (common in threads)
      processedMessage = processedMessage.replace(/(\d+\.\s)/g, '\n$1');
      
      // Handle bullet point patterns
      processedMessage = processedMessage.replace(/([•·▪▫‣⁃]\s)/g, '\n$1');
      
      // Handle paragraph breaks (double spaces often indicate intended line breaks)
      processedMessage = processedMessage.replace(/\s{2,}/g, '\n');
      
      // Clean up multiple consecutive newlines
      processedMessage = processedMessage.replace(/\n{3,}/g, '\n\n');
      
      // Trim leading/trailing whitespace and newlines
      return processedMessage.trim();
    };

    let threadText = '';
    
    tweets.forEach((tweet, index) => {
      // Add tweet number for multi-tweet threads
      if (tweets.length > 1) {
        threadText += `${index + 1}/${tweets.length}\n\n`;
      }
      
      // Clean and format the tweet text by removing links while preserving line breaks
      const cleanedText = formatTweetText(tweet.text);
      threadText += cleanedText;
      
      // Add separator between tweets (except for the last one)
      if (index < tweets.length - 1) {
        threadText += '\n\n---\n\n';
      }
    });

    return threadText;
  }

  // Generate thread summary
  generateThreadSummary(tweets) {
    if (!tweets || tweets.length === 0) {
      return null;
    }

    const firstTweet = tweets[0];
    const totalMetrics = tweets.reduce((sum, tweet) => ({
      likes: sum.likes + tweet.metrics.likeCount,
      retweets: sum.retweets + tweet.metrics.retweetCount,
      replies: sum.replies + tweet.metrics.replyCount,
      views: sum.views + tweet.metrics.viewCount
    }), { likes: 0, retweets: 0, replies: 0, views: 0 });

    return {
      author: firstTweet.author,
      tweetCount: tweets.length,
      totalMetrics,
      firstTweetText: firstTweet.text,
      threadPreview: tweets.slice(0, 3).map(tweet => ({
        id: tweet.id,
        text: tweet.text.slice(0, 100) + (tweet.text.length > 100 ? '...' : ''),
        metrics: tweet.metrics
      }))
    };
  }
}

export default ThreadService; 